import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Music, Handshake, DollarSign, TrendingUp, MapPin, Calculator, Search, FileText, Users, Building, Mic2, Calendar } from "lucide-react";
import { type ShowWithDetails } from "@shared/schema";
import { useState, useEffect, useRef, useCallback } from "react";
// Navbar now imported globally in App.tsx
import ShowCard from "@/components/show-card";
import ShowModal from "@/components/show-modal";
import FeaturedShowsCarousel from "@/components/featured-shows-carousel";


// Custom hook for counting animation
function useCountUp(endValue: number, duration: number = 2000, delay: number = 0) {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible || !endValue) return;

    const startTime = Date.now() + delay;
    const animate = () => {
      const now = Date.now();
      const elapsed = Math.max(0, now - startTime);
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(easeOutQuart * endValue);
      
      setCount(currentCount);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [endValue, duration, delay, isVisible]);

  return { count, elementRef };
}

export default function Home() {
  const { user } = useAuth();
  const [selectedShow, setSelectedShow] = useState<ShowWithDetails | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [searchGenre, setSearchGenre] = useState("");
  const [searchLocation, setSearchLocation] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const heroRef = useRef<HTMLElement>(null);

  const { data: featuredShows = [] } = useQuery({
    queryKey: ["/api/shows/featured"],
    enabled: true,
  });

  const { data: stats } = useQuery({
    queryKey: ["/api/stats"],
    enabled: true,
  });

  // Type-safe stats with fallback values
  const statsData = stats as { sponsorableShows?: number; activeArtists?: number; availableVenues?: number; reachableFans?: number } || {};

  // Counting animations for stats
  const showsCount = useCountUp(statsData?.sponsorableShows || 0, 2000, 0);
  const artistsCount = useCountUp(statsData?.activeArtists || 0, 2000, 200);
  const venuesCount = useCountUp(statsData?.availableVenues || 0, 2000, 400);
  const fansCount = useCountUp(statsData?.reachableFans || 0, 2500, 600);

  const handleViewDetails = (show: ShowWithDetails) => {
    setSelectedShow(show);
    setShowModal(true);
  };

  const handleAddToProposal = (show: ShowWithDetails) => {
    // For now, just close the modal and redirect to browse page
    setShowModal(false);
    window.location.href = "/browse-artists";
  };

  const handleSearch = async () => {
    const params = new URLSearchParams();
    if (searchGenre) params.set("genre", searchGenre);
    if (searchLocation) params.set("location", searchLocation);
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);

    window.location.href = `/browse-artists?${params.toString()}`;
  };

  // Interactive mouse tracking for background effects only
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const heroSection = heroRef.current;
      if (!heroSection) return;

      const rect = heroSection.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;

      // Update CSS custom properties for interactive background
      heroSection.style.setProperty('--mouse-x', `${x}%`);
      heroSection.style.setProperty('--mouse-y', `${y}%`);
    };

    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <div className="min-h-screen bg-neutral-50">

      
      {/* Hero Section */}
      <section ref={heroRef} className="animated-gradient-mesh interactive-bg py-16 pt-32">
        {/* Floating Particles */}
        <div className="floating-particles">
          <div className="particle text-white/20 text-2xl">♪</div>
          <div className="particle text-white/20 text-xl">♫</div>
          <div className="particle text-white/20 text-lg">♪</div>
          <div className="particle text-white/20 text-2xl">♬</div>
          <div className="particle text-white/20 text-xl">♪</div>
          <div className="particle text-white/20 text-lg">♫</div>
          <div className="particle text-white/20 text-2xl">♪</div>
          <div className="particle text-white/20 text-xl">♬</div>
          <div className="particle text-white/20 text-lg">♪</div>
          <div className="particle text-white/20 text-xl"><Mic2 className="w-5 h-5" /></div>
          <div className="particle text-white/20 text-lg"><Building className="w-4 h-4" /></div>
          <div className="particle text-white/20 text-2xl"><MapPin className="w-6 h-6" /></div>
          <div className="particle text-white/20 text-lg"><Mic2 className="w-4 h-4" /></div>
          <div className="particle text-white/20 text-xl"><Building className="w-5 h-5" /></div>
          <div className="particle text-white/20 text-sm"><MapPin className="w-3 h-3" /></div>
        </div>
        
        {/* Wave Overlay */}
        <div className="wave-overlay"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-archivo text-white mb-4">Find Your Perfect Music Sponsorship</h2>
            <p className="text-xl font-work-sans text-white/90 max-w-2xl mx-auto">
              Discover sponsorable artist shows across the country. Filter by genre, location, date range, and audience demographics.
            </p>
          </div>
          
          <div className="glass-morphism-strong rounded-xl p-4 sm:p-6 max-w-6xl mx-auto mb-8 advanced-card-hover">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
              <div className="relative">
                <label className="block text-sm font-medium text-neutral-700 mb-2">Genre</label>
                <div className="relative">
                  <Music className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    value={searchGenre}
                    onChange={(e) => setSearchGenre(e.target.value)}
                    placeholder="e.g., rock, pop or electronic, indie"
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="relative">
                <label className="block text-sm font-medium text-neutral-700 mb-2">Location</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    value={searchLocation}
                    onChange={(e) => setSearchLocation(e.target.value)}
                    placeholder="City, State (e.g., Austin, TX)"
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="relative">
                <label className="block text-sm font-medium text-neutral-700 mb-2">From Date</label>
                <Input 
                  type="date" 
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>
              <div className="relative">
                <label className="block text-sm font-medium text-neutral-700 mb-2">To Date</label>
                <Input 
                  type="date" 
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
              <div className="flex items-end sm:col-span-2 lg:col-span-1">
                <Button variant="primary" className="w-full text-sm sm:text-base font-medium hover:shadow-xl transition-all duration-300 hover:-translate-y-1 active:translate-y-0 active:shadow-md" onClick={handleSearch}>
                  Search Shows
                </Button>
              </div>
            </div>
          </div>

          {/* Browse Methods */}
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Browse by Audience */}
              <Link href="/personas">
                <div className="group relative glass-morphism hover:glass-morphism-strong border border-white/20 hover:border-green-400/30 rounded-xl p-5 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer advanced-card-hover browse-card-green">
                  <div className="flex items-center justify-center w-10 h-10 bg-green-100 group-hover:bg-green-200 rounded-lg mx-auto mb-3 transition-all duration-300">
                    <Users className="h-5 w-5 text-green-600 group-hover:scale-110 transition-all duration-300" />
                  </div>
                  <h4 className="text-base font-archivo text-white group-hover:text-neutral-900 text-center mb-2 transition-colors duration-300">
                    Browse by Audience
                  </h4>
                  <p className="text-xs text-white/80 group-hover:text-neutral-600 text-center leading-relaxed transition-colors duration-300">
                    Find artists that match your target audience demographics and interests
                  </p>
                  <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-green-500 to-emerald-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 rounded-b-xl"></div>
                </div>
              </Link>

              {/* Browse by Artist */}
              <Link href="/browse-artists">
                <div className="group relative glass-morphism hover:glass-morphism-strong border border-white/20 hover:border-primary/30 rounded-xl p-5 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer advanced-card-hover browse-card-pink">
                  <div className="flex items-center justify-center w-10 h-10 bg-pink-100 group-hover:bg-pink-200 rounded-lg mx-auto mb-3 transition-all duration-300">
                    <Mic2 className="h-5 w-5 text-pink-600 group-hover:scale-110 transition-all duration-300" />
                  </div>
                  <h4 className="text-base font-archivo text-white group-hover:text-neutral-900 text-center mb-2 transition-colors duration-300">
                    Browse by Artist
                  </h4>
                  <p className="text-xs text-white/80 group-hover:text-neutral-600 text-center leading-relaxed transition-colors duration-300">
                    Browse by your favorite artists and discover their upcoming shows
                  </p>
                  <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-primary to-secondary scale-x-0 group-hover:scale-x-100 transition-transform duration-300 rounded-b-xl"></div>
                </div>
              </Link>

              {/* Browse by Venue */}
              <Link href="/browse-venues">
                <div className="group relative glass-morphism hover:glass-morphism-strong border border-white/20 hover:border-secondary/30 rounded-xl p-5 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer advanced-card-hover browse-card-purple">
                  <div className="flex items-center justify-center w-10 h-10 bg-purple-100 group-hover:bg-purple-200 rounded-lg mx-auto mb-3 transition-all duration-300">
                    <Building className="h-5 w-5 text-purple-600 group-hover:scale-110 transition-all duration-300" />
                  </div>
                  <h4 className="text-base font-archivo text-white group-hover:text-neutral-900 text-center mb-2 transition-colors duration-300">
                    Browse by Venue
                  </h4>
                  <p className="text-xs text-white/80 group-hover:text-neutral-600 text-center leading-relaxed transition-colors duration-300">
                    Explore venues in your target markets and see all upcoming shows
                  </p>
                  <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-secondary to-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-300 rounded-b-xl"></div>
                </div>
              </Link>

              {/* Browse by Market */}
              <Link href="/browse-markets">
                <div className="group relative glass-morphism hover:glass-morphism-strong border border-white/20 hover:border-primary/30 rounded-xl p-5 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer advanced-card-hover browse-card-blue">
                  <div className="flex items-center justify-center w-10 h-10 bg-blue-100 group-hover:bg-blue-200 rounded-lg mx-auto mb-3 transition-all duration-300">
                    <MapPin className="h-5 w-5 text-blue-600 group-hover:scale-110 transition-all duration-300" />
                  </div>
                  <h4 className="text-base font-archivo text-white group-hover:text-neutral-900 text-center mb-2 transition-colors duration-300">
                    Browse by Market
                  </h4>
                  <p className="text-xs text-white/80 group-hover:text-neutral-600 text-center leading-relaxed transition-colors duration-300">
                    Target specific markets and DMAs to reach your ideal demographics
                  </p>
                  <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 rounded-b-xl"></div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Streamlined Platform Insights - Dark Theme */}
      <section className="py-8 bg-gradient-to-r from-neutral-900 via-neutral-800 to-neutral-900 relative overflow-hidden">
        {/* Subtle dark background effects */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-4 left-10 w-20 h-20 bg-primary rounded-full blur-2xl animate-float"></div>
          <div className="absolute top-8 right-20 w-16 h-16 bg-secondary rounded-full blur-2xl animate-float" style={{animationDelay: '2s'}}></div>
          <div className="absolute bottom-4 left-1/3 w-18 h-18 bg-primary rounded-full blur-2xl animate-float" style={{animationDelay: '4s'}}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Sponsorable Shows */}
            <div className="group text-center">
              <div className="flex items-center justify-center w-10 h-10 bg-blue-900/30 rounded-lg mx-auto mb-2 transition-all duration-300 group-hover:bg-blue-800/40 group-hover:scale-110">
                <Calendar className="h-5 w-5 text-blue-400 transition-all duration-300 group-hover:scale-110" />
              </div>
              <div ref={showsCount.elementRef} className="text-2xl font-bold text-white mb-1">
                {showsCount.count}
              </div>
              <div className="text-xs text-neutral-300">Sponsorable Shows</div>
            </div>
            
            {/* Active Artists */}
            <div className="group text-center">
              <div className="flex items-center justify-center w-10 h-10 bg-pink-900/30 rounded-lg mx-auto mb-2 transition-all duration-300 group-hover:bg-pink-800/40 group-hover:scale-110">
                <Mic2 className="h-5 w-5 text-pink-400 transition-all duration-300 group-hover:scale-110" />
              </div>
              <div ref={artistsCount.elementRef} className="text-2xl font-bold text-white mb-1">
                {artistsCount.count}
              </div>
              <div className="text-xs text-neutral-300">Active Artists</div>
            </div>
            
            {/* Available Venues */}
            <div className="group text-center">
              <div className="flex items-center justify-center w-10 h-10 bg-purple-900/30 rounded-lg mx-auto mb-2 transition-all duration-300 group-hover:bg-purple-800/40 group-hover:scale-110">
                <Building className="h-5 w-5 text-purple-400 transition-all duration-300 group-hover:scale-110" />
              </div>
              <div ref={venuesCount.elementRef} className="text-2xl font-bold text-white mb-1">
                {venuesCount.count}
              </div>
              <div className="text-xs text-neutral-300">Available Venues</div>
            </div>
            
            {/* Reachable Fans */}
            <div className="group text-center">
              <div className="flex items-center justify-center w-10 h-10 bg-green-900/30 rounded-lg mx-auto mb-2 transition-all duration-300 group-hover:bg-green-800/40 group-hover:scale-110">
                <Users className="h-5 w-5 text-green-400 transition-all duration-300 group-hover:scale-110" />
              </div>
              <div ref={fansCount.elementRef} className="text-2xl font-bold text-white mb-1">
                {fansCount.count.toLocaleString()}
              </div>
              <div className="text-xs text-neutral-300">Reachable Fans</div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Shows */}
      <section className="py-16 bg-gradient-to-b from-white to-neutral-50 relative overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-32 h-32 bg-primary rounded-full blur-3xl animate-float"></div>
          <div className="absolute top-40 right-20 w-24 h-24 bg-secondary rounded-full blur-3xl animate-float" style={{animationDelay: '2s'}}></div>
          <div className="absolute bottom-20 left-1/3 w-28 h-28 bg-primary rounded-full blur-3xl animate-float" style={{animationDelay: '4s'}}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-archivo text-neutral-900 mb-4 animate-bounce-in">Top-Rated Shows</h3>
            <p className="text-lg font-work-sans text-neutral-600 max-w-2xl mx-auto">
              Featured artists with upcoming shows, ranked by performance impact, show date, and career stage
            </p>
          </div>
          
          <FeaturedShowsCarousel 
            shows={featuredShows as ShowWithDetails[]}
            onViewDetails={handleViewDetails}
          />
        </div>
      </section>

      {/* How Easy It Is Section */}
      <section className="py-20 bg-gradient-to-br from-neutral-50 via-white to-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-archivo text-neutral-900 mb-6">
              The Easiest Way to Sponsor Live Music
            </h3>
            <p className="text-xl font-work-sans text-neutral-600 max-w-4xl mx-auto leading-relaxed">
              Whether you know exactly what you're looking for or need help maximizing your budget, 
              The MAX Platform™ helps you find the right sponsorship to reach your target audience...
              <span className="font-semibold text-primary"> and get the highest ROI possible.</span>
            </p>
          </div>

          <div className="mb-8 easy-section">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Step 1 */}
              <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-neutral-100 advanced-card-hover relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full mx-auto mb-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group-hover:from-blue-600 group-hover:to-blue-800">
                    <span className="text-2xl font-bold text-white drop-shadow-sm">1</span>
                  </div>
                  <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mx-auto mb-4 group-hover:animate-bounce-in">
                    <Search className="h-6 w-6 text-blue-600" />
                  </div>
                  <h5 className="text-xl font-archivo text-neutral-900 mb-4 text-center">
                    Smart Discovery
                  </h5>
                  <p className="text-neutral-600 font-work-sans leading-relaxed text-center">
                    Search for sponsorship opportunities by artist, venue, or market with advanced audience filtering
                    to ensure the best match. Our platform analytics help you find artists
                    with the highest audience engagement and reach.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-neutral-100 advanced-card-hover relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-500 to-green-700 rounded-full mx-auto mb-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group-hover:from-green-600 group-hover:to-green-800">
                    <span className="text-2xl font-bold text-white drop-shadow-sm">2</span>
                  </div>
                  <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg mx-auto mb-4 group-hover:animate-bounce-in">
                    <Calculator className="h-6 w-6 text-green-600" />
                  </div>
                  <h5 className="text-xl font-archivo text-neutral-900 mb-4 text-center">
                    Smart Budgeting
                  </h5>
                  <p className="text-neutral-600 font-work-sans leading-relaxed text-center">
                    Save shows and use our dynamic pricing calculator to ensure you're maximizing your budget. 
                    We'll help you along the way, making the nuances of music sponsorship 
                    <strong> simple to navigate</strong>.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-neutral-100 advanced-card-hover relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-700 rounded-full mx-auto mb-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group-hover:from-purple-600 group-hover:to-purple-800">
                    <span className="text-2xl font-bold text-white drop-shadow-sm">3</span>
                  </div>
                  <div className="flex items-center justify-center w-12 h-12 bg-purple-100 rounded-lg mx-auto mb-4 group-hover:animate-bounce-in">
                    <FileText className="h-6 w-6 text-purple-600" />
                  </div>
                  <h5 className="text-xl font-archivo text-neutral-900 mb-4 text-center">
                    Smart Proposals
                  </h5>
                  <p className="text-neutral-600 font-work-sans leading-relaxed text-center">
                    Create proposals to detail your campaign scope and get dynamic pricing with our proprietary 
                    <strong> MAXtimate™ pricing</strong>. Generate detailed PDFs based on campaign and brand focus 
                    to get your team aligned.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Step 4 */}
          <div className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-neutral-100 advanced-card-hover overflow-hidden text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-pink-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-pink-500 to-pink-700 rounded-full mx-auto mb-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group-hover:from-pink-600 group-hover:to-pink-800">
                <span className="text-2xl font-bold text-white drop-shadow-sm">4</span>
              </div>
              <div className="flex items-center justify-center w-12 h-12 bg-pink-100 rounded-lg mx-auto mb-4 group-hover:animate-bounce-in">
                <Handshake className="h-6 w-6 text-pink-600" />
              </div>
              <h4 className="text-2xl font-archivo text-neutral-900 mb-4">
                We Do The Rest
              </h4>
              <p className="text-lg font-work-sans text-neutral-600 max-w-3xl mx-auto leading-relaxed">
                We're here to help you along the way, or handle the execution of your proposal when you're ready. 
                From campaign optimization to performance tracking, <strong>The MAX Platform™</strong> makes music 
                sponsorship accessible, effective, and profitable.
              </p>
              <div className="mt-8">
                <Link href="/browse-artists">
                  <Button size="lg" className="btn-max-primary px-8 py-3 text-lg font-medium hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:scale-105">
                    Start Discovering Opportunities
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sponsorship Calculator CTA */}
      <section className="py-16 animated-gradient-mesh interactive-bg relative overflow-hidden">
        {/* Floating Calculator Elements */}
        <div className="floating-particles">
          <div className="particle text-white/10 text-3xl">$</div>
          <div className="particle text-white/10 text-2xl">%</div>
          <div className="particle text-white/10 text-xl">$</div>
          <div className="particle text-white/10 text-3xl">₊</div>
          <div className="particle text-white/10 text-2xl">%</div>
          <div className="particle text-white/10 text-2xl">$</div>
          <div className="particle text-white/10 text-xl">%</div>
          <div className="particle text-white/10 text-3xl">₊</div>
          <div className="particle text-white/10 text-lg">$</div>
          <div className="particle text-white/10 text-2xl">%</div>
          <div className="particle text-white/10 text-xl">₊</div>
          <div className="particle text-white/10 text-3xl">$</div>
          <div className="particle text-white/10 text-lg">%</div>
          <div className="particle text-white/10 text-2xl">₊</div>
          <div className="particle text-white/10 text-xl">$</div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="glass-morphism-strong rounded-2xl p-6 sm:p-8 md:p-12 advanced-card-hover">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <Calculator className="h-8 w-8 text-green-600" />
              </div>
            </div>
            
            <h3 className="text-2xl sm:text-3xl font-archivo text-neutral-900 mb-4">
              What's your sponsorship worth?
            </h3>
            
            <p className="text-lg font-work-sans text-neutral-600 mb-8 max-w-2xl mx-auto">
              Get an instant estimate of what you should be spending on a music sponsorship package. 
              Our calculator considers artist career stage, number of shows, and your marketing objectives 
              to provide accurate cost and impression estimates.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link href="/calculator">
                <Button size="lg" className="btn-max-primary px-6 sm:px-8 py-3 text-base sm:text-lg min-h-[48px] font-medium hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 hover:scale-105 active:scale-100 active:translate-y-0">
                  Calculate Sponsorship Value
                </Button>
              </Link>
              <p className="text-xs sm:text-sm text-neutral-500 text-center sm:text-left">
                Free tool • No signup required • Instant results
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-archivo text-neutral-900 mb-4">Platform Insights</h3>
            <p className="text-lg font-work-sans text-neutral-600">Real-time data on sponsorship opportunities and market trends</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group advanced-card-hover relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <CardContent className="p-6 relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:bg-blue-200 group-hover:scale-110">
                    <Calendar className="h-6 w-6 text-blue-600 transition-all duration-300 group-hover:scale-110" />
                  </div>
                  <span className="text-sm text-green-600 font-medium transition-all duration-300 group-hover:scale-110">Shows</span>
                </div>
                <h4 className="text-2xl font-bold text-neutral-900 mb-2">
                  {statsData?.sponsorableShows || 0}
                </h4>
                <p className="text-neutral-600">Sponsorable Shows</p>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group advanced-card-hover relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-pink-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <CardContent className="p-6 relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:bg-pink-200 group-hover:scale-110">
                    <Mic2 className="h-6 w-6 text-pink-600 transition-all duration-300 group-hover:scale-110" />
                  </div>
                  <span className="text-sm text-green-600 font-medium transition-all duration-300 group-hover:scale-110">Artists</span>
                </div>
                <h4 className="text-2xl font-bold text-neutral-900 mb-2">
                  {statsData?.activeArtists || 0}
                </h4>
                <p className="text-neutral-600">Active Artists</p>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group advanced-card-hover relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <CardContent className="p-6 relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:bg-purple-200 group-hover:scale-110">
                    <Building className="h-6 w-6 text-purple-600 transition-all duration-300 group-hover:scale-110" />
                  </div>
                  <span className="text-sm text-green-600 font-medium transition-all duration-300 group-hover:scale-110">Venues</span>
                </div>
                <h4 className="text-2xl font-bold text-neutral-900 mb-2">
                  {statsData?.availableVenues || 0}
                </h4>
                <p className="text-neutral-600">Available Venues</p>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group advanced-card-hover relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <CardContent className="p-6 relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:bg-green-200 group-hover:scale-110">
                    <Users className="h-6 w-6 text-green-600 transition-all duration-300 group-hover:scale-110" />
                  </div>
                  <span className="text-sm text-green-600 font-medium transition-all duration-300 group-hover:scale-110">Capacity</span>
                </div>
                <h4 className="text-2xl font-bold text-neutral-900 mb-2">
                  {statsData?.reachableFans?.toLocaleString() || 0}
                </h4>
                <p className="text-neutral-600">Reachable Fans</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {!user && (
        <section className="py-16 bg-neutral-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h3 className="text-3xl font-bold text-neutral-900 mb-4">Ready to Get Started?</h3>
            <p className="text-lg text-neutral-600 mb-8 max-w-2xl mx-auto">
              Join thousands of brands already using The MAX Platform™ to find the perfect sponsorship opportunities.
            </p>
            <div className="flex justify-center space-x-4">
              <Link href="/register">
                <Button size="lg">Get Started</Button>
              </Link>
              <Link href="/browse">
                <Button variant="outline" size="lg">Browse Shows</Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      <ShowModal
        show={selectedShow}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onAddToProposal={handleAddToProposal}
      />
    </div>
  );
}
